// React provider + hooks for the client-side preferences store (fn-72.1).
//
// **Shape mirrors `packages/ui/src/internal/context.tsx`:** a `createContext`
// with a sentinel `null` default + hooks that THROW an actionable error when
// no provider is mounted (the silent-null alternative produces undebuggable
// downstream behavior). The throwing contract is intentional: the
// presentational `*View` components stay prop-driven, so only thin `*Live`
// wrappers and the always-mounted layout call these hooks — all of which sit
// inside the app-wide `<PreviewFeaturesProvider>` mounted in `main.tsx`.
//
// The provider holds NO state of its own — the store is a module singleton.
// It exists solely as the "is a provider mounted?" marker that lets the hooks
// fail fast, and as the React-subscription boundary (each hook calls
// `useSyncExternalStore` directly against the singleton).

import {
  type ReactElement,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore,
} from 'react';

import {
  type PrefsSnapshot,
  getServerSnapshot,
  getSnapshot,
  setFavorite as storeSetFavorite,
  setPreviewFeatures as storeSetPreviewFeatures,
  toggleFavorite as storeToggleFavorite,
  togglePreviewFeatures as storeTogglePreviewFeatures,
  subscribe,
} from './store.js';

/** Sentinel context value — `true` once a provider is mounted. `null` means
 *  "no provider", which the hooks treat as a programmer error. */
const PrefsContext = createContext<boolean | null>(null);

export interface PreviewFeaturesProviderProps {
  readonly children: ReactNode;
}

/**
 * App-wide preferences provider. Mount ONCE around `<App/>` (see
 * `main.tsx`). Has no auth dependency — preferences are per-browser and
 * available before login — so app-wide placement is correct.
 */
export function PreviewFeaturesProvider(props: PreviewFeaturesProviderProps): ReactElement {
  return <PrefsContext.Provider value={true}>{props.children}</PrefsContext.Provider>;
}

/** Assert a provider is mounted; throw an actionable error otherwise. */
function assertProvider(hook: string): void {
  if (useContext(PrefsContext) === null) {
    throw new Error(
      `@sb-ai-platform/console: ${hook} called with no <PreviewFeaturesProvider> mounted. Wrap your tree in <PreviewFeaturesProvider> (see main.tsx) before calling prefs hooks.`,
    );
  }
}

/** Subscribe to the live prefs snapshot (`Object.is`-stable across renders). */
function usePrefsSnapshot(): PrefsSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export interface PreviewFeatures {
  /** Whether preview features are visible. Default `false`. */
  readonly enabled: boolean;
  /** Set the flag to an explicit value. */
  readonly setEnabled: (enabled: boolean) => void;
  /** Flip the flag. */
  readonly toggle: () => void;
}

/**
 * Read + control the preview-features flag. Default `false`. Throws if no
 * `<PreviewFeaturesProvider>` is mounted.
 */
export function usePreviewFeatures(): PreviewFeatures {
  assertProvider('usePreviewFeatures()');
  const snapshot = usePrefsSnapshot();
  const setEnabled = useCallback((enabled: boolean): void => {
    storeSetPreviewFeatures(enabled);
  }, []);
  const toggle = useCallback((): void => {
    storeTogglePreviewFeatures();
  }, []);
  return { enabled: snapshot.previewFeatures, setEnabled, toggle };
}

export interface Favorites {
  /** The current favorite keys (opaque strings). `Object.is`-stable. */
  readonly favorites: ReadonlySet<string>;
  /** Whether `key` is currently favorited. */
  readonly isFavorite: (key: string) => boolean;
  /** Toggle `key`'s favorite membership. */
  readonly toggleFavorite: (key: string) => void;
  /** Set `key`'s favorite membership to an explicit value. */
  readonly setFavorite: (key: string, favorite: boolean) => void;
}

/**
 * Read + control the favorites set. Keys are OPAQUE strings — the key
 * convention (e.g. `agent:<id>`) is the consumer's concern, so this hook
 * stays generic. Throws if no `<PreviewFeaturesProvider>` is mounted.
 */
export function useFavorites(): Favorites {
  assertProvider('useFavorites()');
  const snapshot = usePrefsSnapshot();
  const favorites = snapshot.favorites;
  const isFavorite = useCallback((key: string): boolean => favorites.has(key), [favorites]);
  const toggleFavorite = useCallback((key: string): void => {
    storeToggleFavorite(key);
  }, []);
  const setFavorite = useCallback((key: string, favorite: boolean): void => {
    storeSetFavorite(key, favorite);
  }, []);
  return { favorites, isFavorite, toggleFavorite, setFavorite };
}
